import React, { useState, useEffect, useMemo } from "react";
import {
  View,
  Text,
  TouchableOpacity,
  TextInput,
  FlatList,
  Platform,
  ActivityIndicator,
} from "react-native";
import { Ionicons } from "@expo/vector-icons";
import Modal from "react-native-modal";
import { Row } from "./all";
import { normalize } from "../utils/normalize";
import { WORLD_COUNTRIES_ARRAY, getWorldCountryData } from "../constants/worldCountries";
import { getCountries } from "../services/shared";
import { useQuery } from "@tanstack/react-query";

const CountrySelect = (props) => {
  const [showModal, setShowModal] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [selectedCountry, setSelectedCountry] = useState(null);

  // Optional: Fetch countries from API if useApiCountries prop is true
  const {
    data: apiCountriesData,
    isLoading: apiLoading,
    error: apiError,
  } = useQuery(
    "countries",
    getCountries,
    {
      enabled: props.useApiCountries === true,
      staleTime: 60 * 60 * 1000, // Cache for 1 hour
    }
  );

  // Determine which countries list to use
  const countriesList = useMemo(() => {
    if (props.useApiCountries && apiCountriesData?.data?.data) {
      // Transform API data to match our format
      return apiCountriesData.data.data.map(country => ({
        code: country.code,
        name: country.name,
        flag: country.flag || '🏳️',
      }));
    }
    return WORLD_COUNTRIES_ARRAY;
  }, [props.useApiCountries, apiCountriesData]);

  // Filter countries based on search text
  const filteredCountries = useMemo(() => {
    if (!searchText) return countriesList;
    
    const term = searchText.toLowerCase();
    return countriesList.filter(
      (country) =>
        country.name.toLowerCase().includes(term) ||
        country.code.toLowerCase().includes(term)
    );
  }, [searchText, countriesList]);

  useEffect(() => {
    // Set selected country from props value
    if (props.value) {
      const country = getWorldCountryData(props.value) || 
        countriesList.find(c => c.code === props.value);
      setSelectedCountry(country);
    }
  }, [props.value, countriesList]);

  const handleCountrySelect = (country) => {
    setSelectedCountry(country);
    props.onChange && props.onChange(country.code);
    setShowModal(false);
    setSearchText("");
  };

  const getDisplayText = () => {
    if (!selectedCountry) {
      return props.placeholder || "Select Country";
    }
    return `${selectedCountry.flag} ${selectedCountry.name}`;
  };

  const renderCountryItem = ({ item }) => (
    <TouchableOpacity
      onPress={() => handleCountrySelect(item)}
      style={{
        paddingVertical: 16,
        paddingHorizontal: 24,
        borderBottomWidth: 1,
        borderBottomColor: "#F0F0F0",
      }}
    >
      <Row>
        <Text style={{ fontSize: normalize(20), marginRight: 12 }}>
          {item.flag}
        </Text>
        <View style={{ flex: 1 }}>
          <Text
            style={{
              fontSize: normalize(16),
              color: "#333",
            }}
          >
            {item.name}
          </Text>
          <Text
            style={{
              fontSize: normalize(12),
              color: "#666",
              marginTop: 2,
            }}
          >
            {item.code}
          </Text>
        </View>
        {selectedCountry?.code === item.code && (
          <Ionicons name="checkmark" size={20} color="#4B9BFF" />
        )}
      </Row>
    </TouchableOpacity>
  );

  const renderModal = () => (
    <Modal
      isVisible={showModal}
      onBackdropPress={() => {
        setShowModal(false);
        setSearchText("");
      }}
      style={{ margin: 0, justifyContent: "flex-end" }}
      animationIn="slideInUp"
      animationOut="slideOutDown"
      useNativeDriverForBackdrop={true}
    >
      <View
        style={{
          backgroundColor: "white",
          borderTopLeftRadius: 20,
          borderTopRightRadius: 20,
          height: Platform.OS === "ios" ? "90%" : "85%",
          paddingTop: 20,
        }}
      >
        {/* Header */}
        <View
          style={{
            borderBottomWidth: 1,
            borderBottomColor: "#E5E5E5",
            paddingBottom: 16,
            paddingHorizontal: 24,
          }}
        >
          <Row style={{ justifyContent: "space-between", marginBottom: 16 }}>
            <Text
              style={{
                fontSize: normalize(18),
                fontWeight: "600",
              }}
            >
              Select Country
            </Text>
            <TouchableOpacity
              onPress={() => {
                setShowModal(false);
                setSearchText("");
              }}
            >
              <Ionicons name="close" size={24} color="#666" />
            </TouchableOpacity>
          </Row>

          {/* Search Bar */}
          <View
            style={{
              flexDirection: "row",
              alignItems: "center",
              backgroundColor: "#F5F5F5",
              borderRadius: 8,
              paddingHorizontal: 12,
              paddingVertical: Platform.OS === "ios" ? 12 : 8,
            }}
          >
            <Ionicons name="search" size={20} color="#666" />
            <TextInput
              value={searchText}
              onChangeText={setSearchText}
              placeholder="Search country..."
              placeholderTextColor="#999"
              style={{
                flex: 1,
                marginLeft: 8,
                fontSize: normalize(14),
                color: "#333",
              }}
              autoCorrect={false}
              autoCapitalize="none"
              returnKeyType="search"
            />
            {searchText.length > 0 && (
              <TouchableOpacity onPress={() => setSearchText("")}>
                <Ionicons name="close-circle" size={20} color="#999" />
              </TouchableOpacity>
            )}
          </View>
        </View>

        {/* Countries List */}
        {props.useApiCountries && apiLoading ? (
          <View
            style={{
              flex: 1,
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <ActivityIndicator size="large" color="#4B9BFF" />
            <Text
              style={{
                marginTop: 16,
                fontSize: normalize(14),
                color: "#666",
              }}
            >
              Loading countries...
            </Text>
          </View>
        ) : props.useApiCountries && apiError ? (
          <View
            style={{
              flex: 1,
              justifyContent: "center",
              alignItems: "center",
              paddingHorizontal: 24,
            }}
          >
            <Ionicons name="alert-circle" size={48} color="#FF4B4B" />
            <Text
              style={{
                marginTop: 16,
                fontSize: normalize(16),
                color: "#333",
                textAlign: "center",
              }}
            >
              Failed to load countries
            </Text>
            <Text
              style={{
                marginTop: 8,
                fontSize: normalize(14),
                color: "#666",
                textAlign: "center",
              }}
            >
              Using offline country list
            </Text>
          </View>
        ) : (
          <FlatList
            data={filteredCountries}
            keyExtractor={(item) => item.code}
            renderItem={renderCountryItem}
            keyboardShouldPersistTaps="handled"
            showsVerticalScrollIndicator={false}
            ListEmptyComponent={
              <View
                style={{
                  flex: 1,
                  justifyContent: "center",
                  alignItems: "center",
                  paddingVertical: 48,
                }}
              >
                <Ionicons name="search" size={48} color="#CCC" />
                <Text
                  style={{
                    marginTop: 16,
                    fontSize: normalize(16),
                    color: "#666",
                  }}
                >
                  No countries found
                </Text>
              </View>
            }
          />
        )}
      </View>
    </Modal>
  );

  return (
    <View style={props.style}>
      <TouchableOpacity
        onPress={() => setShowModal(true)}
        style={{
          flexDirection: "row",
          alignItems: "center",
          borderWidth: 1,
          borderColor: props.error ? "red" : "#E5E5E5",
          borderRadius: 8,
          paddingHorizontal: 16,
          paddingVertical: Platform.OS === "ios" ? 16 : 14,
          backgroundColor: "white",
        }}
      >
        <Ionicons name="globe-outline" size={16} color="grey" />
        <Text
          style={{
            flex: 1,
            marginLeft: 12,
            fontSize: normalize(14),
            color: selectedCountry ? "#333" : "#999",
          }}
        >
          {getDisplayText()}
        </Text>
        <Ionicons name="chevron-down" size={20} color="#666" />
      </TouchableOpacity>

      {renderModal()}
    </View>
  );
};

export default CountrySelect;